import { useState } from 'react'

import { api } from '../../api/client'
import type { PatchReportSchedule, PatchReportScheduleInput } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { environmentLabel, useIsTenantAdmin } from './labels'

function tipoLabel(k: string): string {
  switch (k) {
    case 'executive': return t('Sintesi direzionale')
    case 'full': return t('Report completo')
    default: return t('Ai responsabili delle macchine')
  }
}

function giornoLabel(i: number): string {
  return [t('lunedì'), t('martedì'), t('mercoledì'), t('giovedì'), t('venerdì'), t('sabato'), t('domenica')][i] ?? ''
}

function quandoLabel(c: PatchReportScheduleInput['cadence']): string {
  if (c.type === 'weekly') return t('ogni {g} alle {ora}', { g: giornoLabel(c.weekday ?? 0), ora: c.at })
  if (c.type === 'monthly') return t('il giorno {d} di ogni mese alle {ora}', { d: c.day ?? 1, ora: c.at })
  return t('{n} giorni dopo il Patch Tuesday, alle {ora}', { n: c.offset_days ?? 0, ora: c.at })
}

const VUOTO: PatchReportScheduleInput = {
  name: '', enabled: true, kind: 'executive', fmt: 'pdf', days: 30,
  cadence: { type: 'weekly', weekday: 0, at: '07:00' }, timezone: 'Europe/Rome', recipients: [], environment: '',
}

/**
 * Patch reports mailed on a cadence (patch_report_schedule_service): the
 * executive summary, the full report, or one mail per asset owner with the
 * missing patches of their machines.
 */
export function ReportSchedules({ environments }: { environments: string[] }) {
  const state = useAsync(() => api.patchReportSchedules(), [])
  const admin = useIsTenantAdmin()
  const [editing, setEditing] = useState<PatchReportSchedule | 'new' | null>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const esegui = async (azione: () => Promise<unknown>, messaggio: (r: unknown) => string) => {
    setLavoro(true)
    setErrore(null)
    setFatto(null)
    try {
      const r = await azione()
      setFatto(messaggio(r))
      state.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const invia = (s: PatchReportSchedule) => {
    if (!window.confirm(t('Mandare adesso «{nome}» ai suoi destinatari?', { nome: s.name }))) return
    void esegui(() => api.patchReportScheduleSend(s.id), (r) => t('Inviato: {esito}', { esito: (r as { result: string }).result }))
  }
  const eliminaReport = (s: PatchReportSchedule) => {
    if (!window.confirm(t('Eliminare il report pianificato «{nome}»?', { nome: s.name }))) return
    void esegui(() => api.patchReportScheduleDelete(s.id), () => t('Report pianificato eliminato.'))
  }
  const colonne: Column<PatchReportSchedule>[] = [
    { key: 'name', header: t('Report'), value: (s) => s.name, render: (s) => <><strong>{s.name}</strong>{!s.enabled && <span className="muted"> · {t('spento')}</span>}<div className="text-muted patch-sub">{tipoLabel(s.kind)} · {s.fmt.toUpperCase()} · {t('{n} giorni', { n: s.days })}{s.environment && <> · {environmentLabel(s.environment)}</>}</div></> },
    { key: 'when', header: t('Quando'), value: (s) => quandoLabel(s.cadence) },
    { key: 'to', header: t('A chi'), value: (s) => s.recipients.join(', '), render: (s) => <span title={s.recipients.join(', ')}>{s.kind === 'owners' ? t('ai responsabili, riepilogo a {n}', { n: s.recipients.length }) : s.recipients.join(', ')}</span> },
    { key: 'next', header: t('Prossimo invio'), type: 'date', value: (s) => s.next_run_at, render: (s) => formatDateTime(s.next_run_at) },
    { key: 'last', header: t('Ultimo invio'), type: 'date', value: (s) => s.last_sent_at, render: (s) => <>{formatDateTime(s.last_sent_at)}{s.last_result && <div className="text-muted patch-sub">{s.last_result}</div>}</> },
    { key: 'actions', header: '', value: () => '', sort: false, filter: false, render: (s) => admin && (
      <span style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="link" onClick={() => setEditing(s)}>{t('Modifica')}</button>
        <button type="button" className="link" disabled={lavoro} onClick={() => invia(s)}>{t('Invia adesso')}</button>
        <button type="button" className="link" disabled={lavoro} onClick={() => eliminaReport(s)}>{t('Elimina')}</button>
      </span>) },
  ]
  return (
    <Card title={t('Report pianificati')} actions={admin ? <button type="button" onClick={() => setEditing('new')}>{t('Nuovo report pianificato')}</button> : undefined}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Un report che parte da solo per mail, ogni settimana, ogni mese o dopo il Patch Tuesday: la sintesi direzionale, il report completo, oppure una mail per ogni responsabile delle macchine (il proprietario dell’asset, se è un indirizzo e-mail) con le patch che mancano sulle sue, le più gravi prima; i destinatari ricevono chi ha avuto cosa e le macchine senza responsabile.')}
      </p>
      <ErrorBanner error={errore ?? state.error} />
      {fatto && <div className="banner banner--ok">{fatto}</div>}
      {!state.data ? !state.error && <Spinner /> : state.data.length === 0
        ? <EmptyState title={t('Nessun report pianificato.')} />
        : <DataTable rows={state.data} columns={colonne} rowKey={(s) => s.id} conteggio={{ nome: t('report') }} />}
      {editing && <Editor report={editing === 'new' ? null : editing} environments={environments} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); state.reload() }} />}
    </Card>
  )
}

function Editor({ report, environments, onClose, onSaved }: { report: PatchReportSchedule | null; environments: string[]; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<PatchReportScheduleInput>(report ? { ...report } : VUOTO)
  const [destinatari, setDestinatari] = useState((report?.recipients ?? []).join(', '))
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const salva = async () => {
    setLavoro(true)
    setErrore(null)
    try {
      const body = { ...v, recipients: destinatari.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean) }
      if (report) await api.patchReportScheduleUpdate(report.id, body)
      else await api.patchReportScheduleCreate(body)
      onSaved()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Report pianificato')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <h3 className="dob-modal__titolo">{report ? report.name : t('Nuovo report pianificato')}</h3>
          <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={errore} />
        <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void salva() }}>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Nome')}</span>
              <input type="text" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required maxLength={200} /></label>
            <label className="field span-2"><span className="field__label">{t('Report')}</span>
              <select value={v.kind} onChange={(e) => { const k = e.target.value as PatchReportScheduleInput['kind']; setV({ ...v, kind: k, fmt: k === 'executive' ? 'pdf' : v.fmt }) }}>
                <option value="executive">{t('Sintesi direzionale')}</option>
                <option value="full">{t('Report completo')}</option>
                <option value="owners">{t('Ai responsabili delle macchine')}</option>
              </select></label>
            <label className="field span-1"><span className="field__label">{t('Formato')}</span>
              <select value={v.fmt} disabled={v.kind === 'executive'} onChange={(e) => setV({ ...v, fmt: e.target.value as 'pdf' | 'xlsx' })}>
                <option value="pdf">PDF</option><option value="xlsx">Excel</option>
              </select></label>
            <label className="field span-1"><span className="field__label">{t('Periodo (giorni)')}</span>
              <input type="number" min={7} max={365} value={v.days} onChange={(e) => setV({ ...v, days: Number(e.target.value) })} /></label>
            <label className="field span-2"><span className="field__label">{t('Ambiente')}</span>
              <select value={v.environment ?? ''} onChange={(e) => setV({ ...v, environment: e.target.value })}>
                <option value="">{t('tutto il parco')}</option>
                {(v.environment && !environments.includes(v.environment) ? [...environments, v.environment] : environments).map((e) => (
                  <option key={e} value={e}>{environmentLabel(e)}</option>
                ))}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Cadenza')}</span>
              <select value={v.cadence.type} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, type: e.target.value as PatchReportScheduleInput['cadence']['type'] } })}>
                <option value="weekly">{t('ogni settimana')}</option>
                <option value="monthly">{t('ogni mese')}</option>
                <option value="patch_tuesday">{t('dopo il Patch Tuesday (secondo martedì del mese)')}</option>
              </select></label>
            {v.cadence.type === 'weekly' && (
              <label className="field span-1"><span className="field__label">{t('Giorno')}</span>
                <select value={v.cadence.weekday ?? 0} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, weekday: Number(e.target.value) } })}>
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => <option key={i} value={i}>{giornoLabel(i)}</option>)}
                </select></label>
            )}
            {v.cadence.type === 'monthly' && (
              <label className="field span-1"><span className="field__label">{t('Giorno del mese')}</span>
                <input type="number" min={1} max={28} value={v.cadence.day ?? 1} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, day: Number(e.target.value) } })} /></label>
            )}
            {v.cadence.type === 'patch_tuesday' && (
              <label className="field span-1"><span className="field__label">{t('Giorni dopo')}</span>
                <input type="number" min={0} max={27} value={v.cadence.offset_days ?? 0} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, offset_days: Number(e.target.value) } })} /></label>
            )}
            <label className="field span-1"><span className="field__label">{t('Alle')}</span>
              <input type="time" value={v.cadence.at} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, at: e.target.value } })} required /></label>
            <label className="field span-4"><span className="field__label">{v.kind === 'owners' ? t('A chi mandare il riepilogo (e-mail, separate da virgola)') : t('Destinatari (e-mail, separate da virgola)')}</span>
              <input type="text" value={destinatari} onChange={(e) => setDestinatari(e.target.value)} required placeholder={'ciso@ospedale.it, it@ospedale.it'} /></label>
            <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
              <span>{t('Acceso')}</span></label>
          </div>
          <div className="form-dialog__azioni">
            <button type="submit" disabled={lavoro}>{lavoro ? t('Salvo…') : t('Salva')}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
